import { useState } from 'react';
import { observer } from 'mobx-react';
import styled from 'styled-components';
import ScrollSpy from 'react-scrollspy';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import { Toolbar, IconButton, AppBar } from '@material-ui/core';
import { useFlags } from 'launchdarkly-react-client-sdk';
import Cookies from 'js-cookie';

import { useAuth0 } from '@auth0/auth0-react';
import BackgroundImage from '../BackgroundImage';
import { useNavDrawer } from '../../hooks/useNavDrawer';
import { useStores } from '../../hooks/useStores';
import SubHeader from './SubHeaders';
import VolusionLogoIcon from '../../assets/VolusionLogo.svg';
import theme from '../../utils/styles/theme';
import { LOGIN_REDIRECT_COOKIE } from '../../utils/withTokenRequired';
import { Flex } from '../Flex';

import HeaderAction from './HeaderAction';
import HeaderUtils from './HeaderUtils';
import MoreActions from './MoreActions';

const ScrollSpyDiv = styled.div`
  position: absolute;
  top: 72px;
`;

const StyledToolbar = styled(Toolbar)`
  && {
    align-items: center;
    height: ${({ theme }) => theme.sizes[4]}px;
    justify-content: space-between;
    background-color: ${({ scrolled, transparent, color }) =>
      transparent && !scrolled ? 'transparent' : color};
    margin-left: 210px;

    @media screen and (max-width: ${({ theme }) => theme.breakpoints[2]}) {
      margin-left: 0;
    }
  }
`;

const HeaderTitle = styled.div`
  color: white;
  font-weight: bold;
  text-transform: capitalize;
  letter-spacing: 1px;
  margin-left: ${({ theme }) => theme.space[3]}px;
  font-size: ${({ theme }) => theme.fontSizes[4]}px;
`;

const StyledVolusionLogoIcon = styled(({ className }) => (
  <VolusionLogoIcon className={className} />
))`
  height: 20px;
  width: 20px;
  background-color: ${({ transparent }) =>
    transparent ? 'transparent' : undefined};
`;

const StyledAppBar = styled(AppBar)`
  && {
    padding-left: ${({ theme }) => theme.space[3]}px;
    padding-right: ${({ theme }) => theme.space[1]}px;
    background-color: ${({ $backgroundColor, $isEmptyPage }) =>
      $isEmptyPage ? 'transparent' : $backgroundColor};
    box-shadow: ${(props) => (props.$isEmptyPage ? 'unset' : 'none')};
  }
`;

const StyledScrollSpy = styled(ScrollSpy)`
  height: ${({ theme }) => theme.fontSizes[7]}px;
`;

/**
 *
 * @param root0
 * @param root0.children
 * @param root0.hideBackground
 * @param root0.backgroundColor
 * @param root0.backgroundHeight
 * @param root0.headerBackgroundImage
 * @param root0.position
 * @param root0.title
 * @param root0.canSave
 * @param root0.handleBack
 * @param root0.actions
 * @param root0.saveButton
 * @param root0.subHeaderContent
 * @param root0.isRevealed
 * @param root0.transparent
 * @param root0.isEmptyPage
 */
function Header({
  children,
  hideBackground,
  backgroundColor = theme.colors.neutrals[8],
  backgroundHeight,
  headerBackgroundImage,
  position,
  title,
  canSave = false,
  handleBack,
  actions = [],
  saveButton,
  subHeaderContent,
  isRevealed,
  transparent,
  isEmptyPage,
}) {
  const [scrolledPast, setScrolledPast] = useState(false);
  const hasSubHeader = position !== 'static' && subHeaderContent;
  const showSidebarAndIcons = useMediaQuery(
    `(min-width:${theme.breakpoints[2]})`
  );
  const isMobile = useMediaQuery(`(max-width:${theme.breakpoints[1]})`);
  const { logout } = useAuth0();
  const { toggleNav } = useNavDrawer();
  const rootStore = useStores();
  const flags = useFlags();

  const isAlwaysVisible = (action) => !isMobile || action.alwaysVisible;
  const helpCenterActionItem = {
    onClick: () => window.open('https://help.volusion.com/', '_blank'),
    label: 'Help Center',
  };

  const logoutActionItem = {
    label: 'Log out',
    onClick: () => {
      rootStore.clearUser();
      Cookies.remove(LOGIN_REDIRECT_COOKIE);
      logout({ returnTo: process.env.NEXT_PUBLIC_AUTH_LOGOUT_RETURN_URL });
    },
  };

  const defaultMoreActions = [helpCenterActionItem, logoutActionItem];

  const toolbarActions = actions.filter(isAlwaysVisible);
  const moreActions = [
    ...actions.filter((action) => !isAlwaysVisible(action)),
    ...defaultMoreActions,
  ];

  return (
    <>
      <StyledScrollSpy
        componentTag="div"
        items={['past-header']}
        onUpdate={(el) => setScrolledPast(!el)}
      >
        {!hideBackground && (
          <BackgroundImage
            backgroundColor={backgroundColor}
            height={backgroundHeight}
            backgroundImage={headerBackgroundImage}
          />
        )}
        <StyledAppBar
          position={position}
          $isEmptyPage={isEmptyPage}
          $backgroundColor={backgroundColor}
        >
          <StyledToolbar
            disableGutters
            $showSidebarAndIcons={showSidebarAndIcons}
            transparent={transparent}
            $scrolledPast={scrolledPast}
          >
            <Flex justifyContent="space-between" mr={[0, 3]} width="100%">
              <Flex alignItems="center">
                {!showSidebarAndIcons && (
                  <IconButton color="secondary" onClick={toggleNav}>
                    <StyledVolusionLogoIcon />
                  </IconButton>
                )}
                {!canSave && handleBack && (
                  <IconButton
                    onClick={handleBack}
                    color="secondary"
                    data-testid="header-back"
                  >
                    <ArrowBackIcon />
                  </IconButton>
                )}
                {!canSave && (
                  <HeaderTitle data-testid="header-title">{title}</HeaderTitle>
                )}
                {saveButton}
              </Flex>
              <HeaderUtils>{children}</HeaderUtils>
              <Flex>
                {toolbarActions.map((action, i) => (
                  <Flex mr={2} key={`header-action-${i}`}>
                    <HeaderAction {...action} />
                  </Flex>
                ))}
                <MoreActions moreActions={moreActions} />
              </Flex>
            </Flex>
          </StyledToolbar>
        </StyledAppBar>
        <ScrollSpyDiv id="past-header" />
      </StyledScrollSpy>
      {hasSubHeader && (
        <SubHeader
          backgroundColor={backgroundColor}
          content={subHeaderContent}
          width="100%"
          isRevealed={isRevealed}
        />
      )}
    </>
  );
}

export default observer(Header);
